import { useCallback, useState } from "react"

import type { SearchScope } from "@/types/api"

const KEY = "celver-copilot:search-scopes"
const ALL: SearchScope[] = ["sharepoint", "mail", "teams"]
const DEFAULT: SearchScope[] = ["sharepoint"]

function read(): SearchScope[] {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(KEY) ?? "null")
    if (Array.isArray(stored)) {
      const valid = ALL.filter((scope) => stored.includes(scope))
      if (valid.length > 0) return valid
    }
  } catch {
    // unreadable storage or malformed value
  }
  return DEFAULT
}

/**
 * Classic mode: which sources a request searches (SharePoint, own mail, own Teams chats).
 * Remembered per browser; SharePoint only by default. At least one scope stays selected.
 * Storage is a convenience only; failures keep the in-memory value.
 */
export function useSearchScopes() {
  const [scopes, setScopes] = useState<SearchScope[]>(read)

  const toggleScope = useCallback((scope: SearchScope) => {
    setScopes((current) => {
      const next = current.includes(scope) ? current.filter((s) => s !== scope) : ALL.filter((s) => s === scope || current.includes(s))
      if (next.length === 0) return current
      try {
        localStorage.setItem(KEY, JSON.stringify(next))
      } catch {
        // keep the in-memory state
      }
      return next
    })
  }, [])

  return { scopes, toggleScope }
}
